/**
 * "What would an ingest of this configuration record?" — shown above Save in
 * the dataset form.
 *
 * Every mistake the form can encode — a status no column claims, a departure
 * column spelled differently from the workflow, a field the adapter cannot
 * parse — used to surface as warnings on the first sync AFTER the dataset was
 * saved and attached to a team. This panel runs that same ingest over the
 * unsaved form and shows the same panels the datasets page shows after a
 * sync, so the config is fixed before it exists.
 *
 * Three deliberate constraints, the first two shared with `QueryPreviewPanel`:
 *
 * - **A button, never automatically.** Each preview is a full fetch against
 *   the tracker (warm-cached after the first), and a half-edited form is not
 *   a question worth asking. The cost is visible and the user chooses to pay
 *   it.
 * - **A stale result is withdrawn, not shown.** The result is kept beside the
 *   exact request that produced it; once the form differs, the warnings
 *   describe a config that no longer exists. Unlike a query count, a clean
 *   result that silently vanished would read as "still clean", so the
 *   withdrawal is announced rather than silent.
 * - **The counts are shown even when there is no warning.** An ingest that
 *   selects no deliverables, or in which nothing ever reached the departure
 *   column, raises no warning at all and forecasts
 *   nothing. Those two zeros are called out as notes — the backend's own
 *   warnings stay the backend's.
 */

import { useState } from 'react';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';

import { ApiError } from '@/lib/api';
import { useDatasetPreview } from '../hooks';
import type { DatasetPreviewRequest, DatasetPreviewResponse } from '../types';
import { WarningPanels } from './WarningPanels';

/** A preview result together with the exact request that produced it. */
type Snapshot = { key: string; result: DatasetPreviewResponse };

/**
 * The identity of a request, for stale-result detection. Key order is fixed
 * by the builder that produces the body, so structural equality is string
 * equality here.
 */
function keyOf(body: DatasetPreviewRequest): string {
  return JSON.stringify(body);
}

export function DatasetPreviewPanel({
  body,
  disabled,
}: {
  /**
   * The dataset as the API would read it, built live from the form by the
   * same function Save uses — or `null` while the form cannot produce one
   * (unparseable workflow JSON, say), which the form is already reporting.
   */
  body: DatasetPreviewRequest | null;
  disabled: boolean;
}) {
  const preview = useDatasetPreview();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  const currentKey = body ? keyOf(body) : null;
  // The same pre-checks the endpoint runs first, so the button is not
  // offered for a request that would only come back as "enter both queries".
  const complete =
    !!body &&
    body.tracker_source_id.length > 0 &&
    body.deliverables_query.trim().length > 0 &&
    body.work_items_query.trim().length > 0;
  const runnable = complete && !disabled && !preview.isPending;

  // Derived, not an effect: comparing against the snapshot's own key is what
  // makes an edited form drop its stale answer, with no state to synchronise.
  const errorFor =
    preview.isError && preview.variables && currentKey === keyOf(preview.variables)
      ? preview.error instanceof ApiError
        ? preview.error.message
        : 'Preview failed.'
      : null;
  // An error about *this* config supersedes an earlier success for the same
  // config — the tracker may have gone away since.
  const current = !errorFor && snapshot && snapshot.key === currentKey ? snapshot.result : null;
  const withdrawn = !!snapshot && snapshot.key !== currentKey && !preview.isPending;

  const run = () => {
    if (!body || !runnable) return;
    const key = keyOf(body);
    preview.mutate(body, { onSuccess: (result) => setSnapshot({ key, result }) });
  };

  return (
    <section aria-labelledby="dataset-preview-heading" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="dataset-preview-heading" className="text-sm font-medium text-text-primary">
            Before you save
          </h3>
          <p className="text-xs text-text-muted">
            Runs the sync against the tracker without saving anything, and shows the warnings it would raise.
          </p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={run} disabled={!runnable}>
          {preview.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" /> Previewing…
            </>
          ) : (
            'Preview warnings'
          )}
        </Button>
      </div>

      {/* Announced politely: the result arrives after an explicit action, so
          a screen-reader user needs to hear it without losing their place. */}
      <div aria-live="polite" className="space-y-3 text-sm">
        {preview.isPending && (
          <p className="text-text-muted">Fetching from the tracker — the first run over a source can take a moment.</p>
        )}

        {errorFor && (
          <p className="text-feedback-error">
            {/* The tracker's own diagnostic, or Save's own workflow refusal,
                verbatim — each names what to fix. */}
            {errorFor}
          </p>
        )}

        {withdrawn && !errorFor && (
          <p className="text-text-muted">
            The form has changed since the last preview — run it again to see the result.
          </p>
        )}

        {current && <PreviewResult result={current} />}
      </div>
    </section>
  );
}

function PreviewResult({ result }: { result: DatasetPreviewResponse }) {
  const n = result.warnings.length;
  return (
    <>
      <p className="text-text-secondary">
        Would import <strong>{result.deliverable_count}</strong> {plural(result.deliverable_count, 'deliverable')} from{' '}
        <strong>{result.work_item_count}</strong> {plural(result.work_item_count, 'work item')}, with{' '}
        <strong>{result.dependency_count}</strong> {plural(result.dependency_count, 'dependency', 'dependencies')};{' '}
        <strong>{result.throughput_total}</strong> {plural(result.throughput_total, 'item')} completed in the throughput
        window.
      </p>

      {result.deliverable_count === 0 && (
        <Note>The deliverables query selected nothing — there would be nothing to forecast.</Note>
      )}
      {result.throughput_total === 0 && (
        <Note>
          Nothing has ever reached the departure column — every team&apos;s throughput would be empty, and the forecast
          cannot run on an empty series.
        </Note>
      )}

      {n === 0 ? (
        <p className="flex items-center gap-2 text-feedback-success">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
          No warnings — a sync of this configuration would run clean.
        </p>
      ) : (
        <>
          <p className="font-medium text-text-primary">
            {n} {plural(n, 'warning')} a sync would raise:
          </p>
          <WarningPanels warnings={result.warnings} />
        </>
      )}
    </>
  );
}

/**
 * A count-derived observation, visually distinct from the backend's warning
 * panels: those are the ingest's own findings, this is the form pointing at
 * a number the ingest reported without complaint.
 */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-text-secondary">
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}
